import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUsername } from '../../auth/auth';

const HarmonySelect = () => {
  const navigate = useNavigate();
  const [targetId, setTargetId] = useState('');
  const [manualMyId, setManualMyId] = useState('');

  const myId = useMemo(() => getCurrentUsername(), []);

  const handleStart = () => {
    const otherId = targetId.trim();
    const effectiveMyId = (myId || manualMyId).trim();

    if (!effectiveMyId) {
      alert('Your ID is missing. Enter it manually to continue.');
      return;
    }

    if (!otherId) {
      alert('Enter the other ID to continue.');
      return;
    }

    if (otherId === effectiveMyId) {
      alert('Use a different ID from your own.');
      return;
    }

    navigate(`/harmony/result?id1=${encodeURIComponent(effectiveMyId)}&id2=${encodeURIComponent(otherId)}`);
  };

  return (
    <div className="harmony-container">
      <div className="harmony-card">
        <div className="select-topline">Ready to compare</div>
        <h1 className="title">Compare with another profile</h1>
        <p className="subtitle">The mobile layout keeps both the input and the primary CTA inside the first viewport.</p>

        <div className="my-id-box">My ID <strong>@{myId || 'unknown'}</strong></div>
        {!myId && (
          <div className="manual-myid">
            <label>Enter my ID manually</label>
            <input
              type="text"
              placeholder="ex: min9808"
              value={manualMyId}
              onChange={(e) => setManualMyId(e.target.value)}
            />
          </div>
        )}

        <div className="input-group">
          <div className="input-field">
            <label>Other ID</label>
            <input
              type="text"
              placeholder="ex: partner123"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStart()}
            />
          </div>
        </div>

        <div className="action-buttons">
          <button className="start-btn primary" onClick={handleStart}>
            Start compatibility check
          </button>
          <button className="start-btn secondary" onClick={() => navigate('/harmony')}>
            Back
          </button>
        </div>
      </div>

      <style jsx>{`
        .harmony-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--mobile-shell-padding-top) var(--mobile-shell-padding-x) var(--mobile-shell-padding-bottom);
          background: radial-gradient(circle at top right, #2e1065, #0f172a),
            radial-gradient(circle at bottom left, #4c1d95, #0f172a);
          color: white;
        }

        .harmony-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 28px;
          padding: 1.3rem 1rem;
          text-align: left;
          max-width: var(--mobile-shell-max);
          width: 100%;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .select-topline {
          display: inline-flex;
          align-items: center;
          padding: 0.42rem 0.72rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          color: #cbd5e1;
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 0.9rem;
        }

        .title {
          font-size: var(--mobile-title-md);
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: #f8fafc;
          line-height: 1.3;
        }

        .subtitle {
          color: #94a3b8;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          line-height: 1.55;
        }

        .my-id-box {
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(147, 197, 253, 0.35);
          border-radius: 14px;
          padding: 0.8rem;
          color: #dbeafe;
          margin-bottom: 1rem;
          font-size: 0.92rem;
        }

        .manual-myid {
          text-align: left;
          margin-bottom: 1rem;
        }

        .manual-myid label {
          display: block;
          margin: 0 0 0.35rem 0.15rem;
          color: #bfdbfe;
          font-size: 0.82rem;
          font-weight: 700;
        }

        .manual-myid input {
          width: 100%;
          padding: 0.85rem;
          border-radius: 12px;
          border: 1px solid rgba(147, 197, 253, 0.35);
          background: rgba(15, 23, 42, 0.6);
          color: white;
          font-size: 0.95rem;
          box-sizing: border-box;
        }

        .input-group {
          margin-bottom: 1rem;
        }

        .input-field {
          text-align: left;
        }

        .input-field label {
          display: block;
          margin-left: 0.2rem;
          margin-bottom: 0.5rem;
          font-size: 0.92rem;
          font-weight: 700;
          color: #cbd5e1;
        }

        .input-field input {
          width: 100%;
          min-height: 52px;
          padding: 0.95rem;
          border-radius: 14px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.05);
          color: white;
          font-size: 1rem;
          outline: none;
          box-sizing: border-box;
        }

        .input-field input:focus {
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          margin-top: 1rem;
        }

        .start-btn {
          width: 100%;
          min-height: var(--mobile-button-height);
          padding: 1rem;
          font-size: 1rem;
          font-weight: 700;
          border-radius: 16px;
          cursor: pointer;
          border: none;
        }

        .start-btn.primary {
          color: white;
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
        }

        .start-btn.secondary {
          color: #e2e8f0;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default HarmonySelect;
